import { Markdown } from '@kanbu/chatbot-ui';
import type {
  GoogleDriveTypeMetadata,
  WebsiteTypeMetadata,
} from '@kanbu/schema';
import {
  ContentType,
  Role,
  SourceType,
  type UpdateFrequency,
} from '@kanbu/schema/enums';
import { DateFormat, formatUtils } from '@kanbu/shared';
import { Trans, useLingui } from '@lingui/react/macro';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import * as Tabs from '@radix-ui/react-tabs';
import { Dialog, Button, toast } from '@utima/ui';
import { ExternalLink, Loader2 } from 'lucide-react';
import { memo, useEffect, useMemo, useState } from 'react';

import { LockedTabLabel } from '@/components';
import { Card } from '@/components/card/Card';
import { DisplayValue } from '@/components/displayValue/DisplayValue';
import { Protected } from '@/components/protected/Protected';
import { AccessPropagationBanner } from '@/features/knowledge-base/components/AccessPropagationBanner';
import { useAccess } from '@/hooks/useAccess';
import { useSubscriptionFeatures } from '@/hooks/useSubscriptionFeatures';
import { trpc } from '@/services/trpc';

import { AgentAccessSelector } from './AgentAccessSelector';
import { AutoUpdateSettingsForm } from './AutoUpdateSettingsForm';
import { DocumentDiffViewerModal } from './DocumentDiffViewerModal';
import { useDocumentMutation } from '../hooks/useDocumentMutation';

export type DocumentContentModalProps = {
  documentId: string | null | undefined;
  onClose: () => void;
  currentChatId?: string;
  defaultTab?: string;
};

type ManualCheckResult = {
  status?: string;
  reason?: string;
  changed?: boolean;
  changePercent?: number;
  thresholdPercent?: number;
};

/**
 * Renders the content of a document in a modal.
 */
export const DocumentContentModal = memo(function DocumentContentModal({
  documentId,
  onClose,
  currentChatId,
  defaultTab,
}: DocumentContentModalProps) {
  const { t } = useLingui();
  const { hasAccess } = useAccess();
  const isSuperAdmin = hasAccess([Role.SuperAdmin]);
  const { isKbAutoUpdatesLocked } = useSubscriptionFeatures();
  const [activeTab, setActiveTab] = useState('content');

  // Reset tab state on close and apply requested tab on open.
  useEffect(() => {
    if (!documentId) {
      setActiveTab('content');

      return;
    }

    setActiveTab(defaultTab ?? 'content');
  }, [defaultTab, documentId]);

  const { data: document, isLoading } = trpc.documents.findOne.useQuery(
    {
      id: documentId!,
      populate: [
        'content',
        'previousContent',
        'metadata',
        'parsingMetadata',
        'summary',
        'shortSummary',
        'chats',
        'organization.id',
      ],
    },
    { enabled: !!documentId },
  );
  const { data: embeddings } = trpc.documents.findAllEmbeddings.useQuery(
    { documentId: documentId! },
    { enabled: !!documentId },
  );

  // Get all available chats for the agent access tab
  const { data: allChats } = trpc.chats.findAll.useQuery({
    where: { organization: document?.organization.id },
    pagination: { pageSize: 100 },
    fields: ['id', 'name'],
  });

  const supportsAutoUpdate =
    document?.type === ContentType.Website ||
    document?.type === ContentType.ExternalDocument;

  // Agent access state
  const [availableForAllAgents, setAvailableForAllAgents] = useState(false);
  const [selectedChatIds, setSelectedChatIds] = useState<string[]>([]);

  // Update document access mutation
  const { updateAgentAccess, updateAutoUpdate, checkDocumentUpdatesNow } =
    useDocumentMutation();

  // Auto-update state
  const [docFrequency, setDocFrequency] = useState<
    UpdateFrequency | null | undefined
  >(null);
  const [docThreshold, setDocThreshold] = useState<number | null | undefined>(
    null,
  );
  const [isDiffOpen, setIsDiffOpen] = useState(false);
  const [lastManualCheckResult, setLastManualCheckResult] =
    useState<ManualCheckResult | null>(null);

  // Compute inherited chat IDs (in effectiveChatIds but not in direct chats)
  const inheritedChatIds = useMemo(() => {
    if (!document) return [];

    const directIds = new Set(
      document.chats
        ? document.chats.map(chat =>
            typeof chat === 'object' ? chat.id : chat,
          )
        : [],
    );

    const effectiveIds: string[] =
      ((document as Record<string, unknown>).effectiveChatIds as string[]) ??
      [];

    return effectiveIds.filter(id => !directIds.has(id));
  }, [document]);

  // Initialize agent access state from document data
  useEffect(() => {
    if (document) {
      setAvailableForAllAgents(!!document.availableForAllAgents);
      const chatIds = document.chats
        ? document.chats.map(chat =>
            typeof chat === 'object' ? chat.id : chat,
          )
        : [];
      setSelectedChatIds(chatIds);
      setDocFrequency(document.updateFrequency ?? null);
      setDocThreshold(document.changeThreshold ?? null);
    }
  }, [document]);

  const handleSaveAutoUpdate = async () => {
    if (!documentId || isKbAutoUpdatesLocked) return;

    await updateAutoUpdate.mutateAsync({
      id: documentId,
      updateFrequency: docFrequency ?? null,
      changeThreshold: isSuperAdmin
        ? (docThreshold ?? null)
        : (document?.changeThreshold ?? null),
    });
  };

  const handleSaveAccess = async () => {
    if (!documentId) return;

    await updateAgentAccess.mutateAsync({
      documentId,
      chatIds: selectedChatIds,
      availableForAllAgents,
    });
  };

  const handleCheckForUpdates = async () => {
    if (!documentId) return;

    try {
      const result = (await checkDocumentUpdatesNow.mutateAsync({
        documentId,
      })) as ManualCheckResult;
      setLastManualCheckResult(result);
      toast.success(t`Document check completed`);
    } catch {
      toast.error(t`Failed to run document check`);
    }
  };

  const content = document?.content?.pages
    .map(page => page.markdown)
    .join('\n\n');
  const websiteMetadata = document?.metadata as WebsiteTypeMetadata | undefined;
  const driveFileId =
    document?.sourceType === SourceType.GoogleDrive
      ? (document.metadata as GoogleDriveTypeMetadata | undefined)?.driveFileId
      : undefined;
  const sourceUrl = driveFileId
    ? `https://drive.google.com/file/d/${encodeURIComponent(driveFileId)}/view`
    : document?.publicUrl;
  const rawDiff =
    document?.type === ContentType.Website
      ? websiteMetadata?.lastRawDiff
      : undefined;
  const previousContent = (
    rawDiff?.before ??
    (websiteMetadata?.compareFormat === 'fc-raw-v1'
      ? undefined
      : document?.previousContent)
  )?.pages
    .map(page => page.markdown)
    .join('\n\n');
  const diffCurrentContent =
    rawDiff?.after.pages.map(page => page.markdown).join('\n\n') ?? content;
  const hasPreviousContent = !!previousContent?.trim();
  const hasCurrentContent = !!diffCurrentContent?.trim();

  return (
    <Dialog.Root open={!!documentId} onOpenChange={onClose}>
      <Dialog.Content className='flex h-[95vh] w-full max-w-[1200px] flex-col overflow-hidden'>
        <Dialog.Header>
          <div className='flex items-center gap-3'>
            <Dialog.Title>{document?.name}</Dialog.Title>
            {sourceUrl && (
              <Button variant='ghost' size='sm' asChild>
                <a href={sourceUrl} target='_blank' rel='noopener noreferrer'>
                  <ExternalLink className='size-4' />
                  <Trans>Open source</Trans>
                </a>
              </Button>
            )}
          </div>
        </Dialog.Header>

        {isLoading ? (
          <div className='flex min-h-40 items-center justify-center text-primary'>
            <Loader2 className='size-10 animate-spin' />
          </div>
        ) : (
          <Tabs.Root
            value={activeTab}
            onValueChange={setActiveTab}
            className='flex min-h-0 flex-1 flex-col'
          >
            <Tabs.List className='border-b border-border'>
              <div className='flex gap-2 px-4'>
                <Tabs.Trigger
                  value='content'
                  className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
                >
                  <Trans>Content</Trans>
                </Tabs.Trigger>
                <Protected roles={[Role.SuperAdmin]}>
                  <Tabs.Trigger
                    value='metadata'
                    className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
                  >
                    <Trans>Metadata</Trans>
                  </Tabs.Trigger>
                  <Tabs.Trigger
                    value='embeddings'
                    className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
                  >
                    <Trans>Embeddings</Trans>
                  </Tabs.Trigger>
                </Protected>
                <Tabs.Trigger
                  value='agents'
                  className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
                >
                  <Trans>Agents with access</Trans>
                </Tabs.Trigger>
                {supportsAutoUpdate && (
                  <Tabs.Trigger
                    value='auto-update'
                    className='px-4 py-2 text-sm font-medium data-[state=active]:border-b-2 data-[state=active]:border-primary'
                  >
                    <LockedTabLabel locked={isKbAutoUpdatesLocked}>
                      <Trans>Auto-update Settings</Trans>
                    </LockedTabLabel>
                  </Tabs.Trigger>
                )}
              </div>
            </Tabs.List>

            <div className='min-h-0 flex-1'>
              <Tabs.Content value='content' className='h-full'>
                <ScrollArea.Root className='h-full'>
                  <ScrollArea.Viewport className='size-full p-4'>
                    <div className='whitespace-pre-wrap'>
                      {content ? (
                        <Markdown>{content}</Markdown>
                      ) : (
                        <Trans>No content available</Trans>
                      )}
                    </div>
                  </ScrollArea.Viewport>
                  <ScrollArea.Scrollbar
                    className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                    orientation='vertical'
                  >
                    <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
                  </ScrollArea.Scrollbar>
                </ScrollArea.Root>
              </Tabs.Content>

              <Tabs.Content value='metadata' className='h-full'>
                <ScrollArea.Root className='h-full'>
                  <ScrollArea.Viewport className='size-full p-4'>
                    <Card
                      title='Metadata'
                      className='mb-6 rounded-lg border border-border p-4'
                    >
                      <div className='space-y-4'>
                        {Object.entries(document?.parsingMetadata || {}).map(
                          ([key, value]) => (
                            <DisplayValue key={key} label={key} size='sm'>
                              {typeof value === 'string' ? (
                                value
                              ) : (
                                <pre className='rounded-lg bg-slate-100 p-2 text-xs'>
                                  {JSON.stringify(value, null, 2)}
                                </pre>
                              )}
                            </DisplayValue>
                          ),
                        )}
                        {Object.entries(document?.metadata || {}).map(
                          ([key, value]) => (
                            <DisplayValue key={key} label={key} size='sm'>
                              {typeof value === 'string' ? (
                                value
                              ) : (
                                <pre className='rounded-lg bg-slate-100 p-2 text-xs'>
                                  {JSON.stringify(value, null, 2)}
                                </pre>
                              )}
                            </DisplayValue>
                          ),
                        )}
                        <DisplayValue label='publicUrl' size='sm'>
                          {document?.publicUrl}
                        </DisplayValue>
                        <DisplayValue label='id' size='sm'>
                          {document?.id}
                        </DisplayValue>
                        <DisplayValue label='uri' size='sm'>
                          {document?.uri}
                        </DisplayValue>
                        <DisplayValue label='scheduledAt' size='sm'>
                          {formatUtils.date(
                            document?.scheduledAt,
                            DateFormat.DateTimeNumericPrecise,
                          )}
                        </DisplayValue>
                        <DisplayValue label='startedParsingAt' size='sm'>
                          {formatUtils.date(
                            document?.startedParsingAt,
                            DateFormat.DateTimeNumericPrecise,
                          )}
                        </DisplayValue>
                        <DisplayValue label='finishedParsingAt' size='sm'>
                          {formatUtils.date(
                            document?.finishedParsingAt,
                            DateFormat.DateTimeNumericPrecise,
                          )}
                        </DisplayValue>
                        <DisplayValue label='startedEmbeddingAt' size='sm'>
                          {formatUtils.date(
                            document?.startedEmbeddingAt,
                            DateFormat.DateTimeNumericPrecise,
                          )}
                        </DisplayValue>
                        <DisplayValue label='finishedEmbeddingAt' size='sm'>
                          {formatUtils.date(
                            document?.finishedEmbeddingAt,
                            DateFormat.DateTimeNumericPrecise,
                          )}
                        </DisplayValue>
                        <DisplayValue label='summary' size='sm'>
                          {document?.summary}
                        </DisplayValue>
                        <DisplayValue label='shortSummary' size='sm'>
                          {document?.shortSummary}
                        </DisplayValue>
                      </div>
                    </Card>
                    <Card title='Content'>
                      <div className='w-full whitespace-pre-wrap rounded-lg font-sans text-sm leading-5'>
                        {content}
                      </div>
                    </Card>
                  </ScrollArea.Viewport>
                  <ScrollArea.Scrollbar
                    className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                    orientation='vertical'
                  >
                    <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
                  </ScrollArea.Scrollbar>
                </ScrollArea.Root>
              </Tabs.Content>

              <Tabs.Content value='embeddings' className='h-full'>
                <ScrollArea.Root className='h-full'>
                  <ScrollArea.Viewport className='size-full p-4'>
                    <div className='rounded-lg border border-border p-4'>
                      <div className='space-y-4'>
                        {embeddings?.map((embedding, index) => (
                          <Card
                            title={`Chunk ${index + 1} (${embedding.uuid})`}
                            key={embedding.uuid}
                          >
                            <pre className='w-full whitespace-pre-wrap rounded-lg bg-slate-100 p-3 text-xs leading-5'>
                              {JSON.stringify(
                                JSON.parse(
                                  embedding.properties.metadata || '{}',
                                ),
                                null,
                                2,
                              )}
                            </pre>
                            <Card
                              title='keywordsContent'
                              className='w-full whitespace-pre-wrap rounded-lg font-sans text-sm leading-5'
                            >
                              {embedding.properties?.keywordsContent as string}
                            </Card>
                            <Card
                              title='embeddingContent'
                              className='w-full whitespace-pre-wrap rounded-lg font-sans text-sm leading-5'
                            >
                              {embedding.properties?.embeddingContent as string}
                            </Card>
                            <Card
                              title='content'
                              className='w-full whitespace-pre-wrap rounded-lg font-sans text-sm leading-5'
                            >
                              {embedding.properties?.content as string}
                            </Card>
                          </Card>
                        ))}
                      </div>
                    </div>
                  </ScrollArea.Viewport>
                  <ScrollArea.Scrollbar
                    className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                    orientation='vertical'
                  >
                    <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
                  </ScrollArea.Scrollbar>
                </ScrollArea.Root>
              </Tabs.Content>

              <Tabs.Content value='agents' className='h-full'>
                <ScrollArea.Root className='h-full'>
                  <ScrollArea.Viewport className='size-full p-4'>
                    <Card
                      title={t`Configure agent access`}
                      className='mb-6 rounded-lg border border-border p-4'
                    >
                      <div className='space-y-6'>
                        <p className='text-sm text-gray-600'>
                          <Trans>
                            Control which agents have access to this document.
                            You can make it available to all agents or select
                            specific ones.
                          </Trans>
                        </p>

                        <AccessPropagationBanner documentId={documentId!} />

                        <div className='md:w-1/3'>
                          <AgentAccessSelector
                            availableForAllAgents={availableForAllAgents}
                            setAvailableForAllAgents={setAvailableForAllAgents}
                            selectedChatIds={selectedChatIds}
                            setSelectedChatIds={setSelectedChatIds}
                            availableAgents={allChats?.items ?? []}
                            currentChatId={currentChatId}
                            inheritedChatIds={inheritedChatIds}
                          />
                        </div>
                        <div className='flex justify-end'>
                          <Button
                            data-cta='admin-document-content-modal-save-access-button'
                            onClick={handleSaveAccess}
                            disabled={updateAgentAccess.isPending}
                          >
                            {updateAgentAccess.isPending ? (
                              <>
                                <Loader2 className='mr-2 size-4 animate-spin' />
                                <Trans>Saving...</Trans>
                              </>
                            ) : (
                              <Trans>Save access settings</Trans>
                            )}
                          </Button>
                        </div>
                      </div>
                    </Card>
                  </ScrollArea.Viewport>
                  <ScrollArea.Scrollbar
                    className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                    orientation='vertical'
                  >
                    <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
                  </ScrollArea.Scrollbar>
                </ScrollArea.Root>
              </Tabs.Content>

              {supportsAutoUpdate && (
                <Tabs.Content value='auto-update' className='h-full'>
                  <ScrollArea.Root className='h-full'>
                    <ScrollArea.Viewport className='size-full p-4'>
                      <div className='space-y-6'>
                        <fieldset
                          disabled={isKbAutoUpdatesLocked}
                          className='space-y-6 disabled:pointer-events-none disabled:opacity-50'
                        >
                          <Card
                            title={t`Auto-update settings`}
                            className='rounded-lg border border-border p-4'
                          >
                            <div className='space-y-6'>
                              <p className='text-sm text-muted-fg'>
                                <Trans>
                                  Override the organization-wide auto-update
                                  settings for this document.
                                </Trans>
                              </p>

                              <div className='md:w-1/2'>
                                <AutoUpdateSettingsForm
                                  mode='single'
                                  showChangeThreshold={isSuperAdmin}
                                  showWebsiteFields={false}
                                  updateFrequency={docFrequency}
                                  changeThreshold={docThreshold}
                                  onUpdateFrequencyChange={setDocFrequency}
                                  onChangeThresholdChange={setDocThreshold}
                                />
                              </div>

                              <div className='flex justify-end'>
                                <Button
                                  onClick={handleSaveAutoUpdate}
                                  disabled={
                                    updateAutoUpdate.isPending ||
                                    isKbAutoUpdatesLocked
                                  }
                                >
                                  {updateAutoUpdate.isPending ? (
                                    <>
                                      <Loader2 className='mr-2 size-4 animate-spin' />
                                      <Trans>Saving...</Trans>
                                    </>
                                  ) : (
                                    <Trans>Save auto-update settings</Trans>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </Card>

                          <Card
                            title={t`Update checks and history`}
                            className='rounded-lg border border-border p-4'
                          >
                            <div className='space-y-6'>
                              <p className='text-sm text-muted-fg'>
                                <Trans>
                                  Run an on-demand check and review the latest
                                  change detection details.
                                </Trans>
                              </p>

                              <div className='grid gap-3 sm:grid-cols-3'>
                                <div className='rounded-md border border-border bg-muted/30 p-3'>
                                  <p className='text-xs font-medium uppercase tracking-wide text-muted-fg'>
                                    <Trans>Last checked</Trans>
                                  </p>
                                  <p className='mt-1 text-sm'>
                                    {document?.lastCheckedAt
                                      ? formatUtils.date(
                                          document.lastCheckedAt,
                                          DateFormat.DateTimeNumericPrecise,
                                        )
                                      : t`No checks yet`}
                                  </p>
                                </div>
                                <div className='rounded-md border border-border bg-muted/30 p-3'>
                                  <p className='text-xs font-medium uppercase tracking-wide text-muted-fg'>
                                    <Trans>Content last changed</Trans>
                                  </p>
                                  <p className='mt-1 text-sm'>
                                    {document?.contentUpdatedAt
                                      ? formatUtils.date(
                                          document.contentUpdatedAt,
                                          DateFormat.DateTimeNumericPrecise,
                                        )
                                      : t`No change detected yet`}
                                  </p>
                                </div>
                                <div className='rounded-md border border-border bg-muted/30 p-3'>
                                  <p className='text-xs font-medium uppercase tracking-wide text-muted-fg'>
                                    <Trans>Next scheduled update</Trans>
                                  </p>
                                  <p className='mt-1 text-sm'>
                                    {document?.nextUpdateAt
                                      ? formatUtils.date(
                                          document.nextUpdateAt,
                                          DateFormat.DateTimeNumericPrecise,
                                        )
                                      : t`Not scheduled`}
                                  </p>
                                </div>
                              </div>

                              <div className='rounded-md border border-border bg-muted/40 p-3 text-sm text-muted-fg'>
                                {document?.lastCheckedAt ? (
                                  hasPreviousContent ? (
                                    <Trans>
                                      A previously detected content change is
                                      available for comparison.
                                    </Trans>
                                  ) : (
                                    <Trans>
                                      Last check did not produce a stored
                                      previous version for comparison.
                                    </Trans>
                                  )
                                ) : (
                                  <Trans>
                                    No update checks have been recorded yet.
                                  </Trans>
                                )}

                                {lastManualCheckResult?.reason ===
                                'baseline_initialized' ? (
                                  <p className='mt-2'>
                                    <Trans>
                                      Comparison baseline saved. Existing
                                      content and embeddings were kept.
                                    </Trans>
                                  </p>
                                ) : lastManualCheckResult?.reason ===
                                  'recently_checked' ? (
                                  <p className='mt-2'>
                                    <Trans>
                                      This document was checked recently or a
                                      check is still running. Try again later.
                                    </Trans>
                                  </p>
                                ) : (
                                  lastManualCheckResult?.changePercent !==
                                    undefined && (
                                    <p className='mt-2'>
                                      <Trans>
                                        Last manual check:{' '}
                                        {lastManualCheckResult.changePercent}%
                                        change (threshold:{' '}
                                        {lastManualCheckResult.thresholdPercent ??
                                          0}
                                        %) .
                                      </Trans>
                                    </p>
                                  )
                                )}
                              </div>

                              <div className='flex justify-end gap-2'>
                                {hasPreviousContent && hasCurrentContent && (
                                  <Button
                                    variant='ghost'
                                    outline
                                    onClick={() => setIsDiffOpen(true)}
                                  >
                                    <Trans>View last-change diff</Trans>
                                  </Button>
                                )}
                                <Button
                                  variant='ghost'
                                  outline
                                  onClick={handleCheckForUpdates}
                                  disabled={checkDocumentUpdatesNow.isPending}
                                >
                                  {checkDocumentUpdatesNow.isPending ? (
                                    <>
                                      <Loader2 className='mr-2 size-4 animate-spin' />
                                      <Trans>Checking...</Trans>
                                    </>
                                  ) : (
                                    <Trans>Check for changes now</Trans>
                                  )}
                                </Button>
                              </div>
                            </div>
                          </Card>
                        </fieldset>
                      </div>
                    </ScrollArea.Viewport>
                    <ScrollArea.Scrollbar
                      className='flex touch-none select-none bg-gray-300 p-1 transition-colors hover:bg-gray-500'
                      orientation='vertical'
                    >
                      <ScrollArea.Thumb className='relative flex-1 rounded-full bg-gray-400' />
                    </ScrollArea.Scrollbar>
                  </ScrollArea.Root>
                </Tabs.Content>
              )}
            </div>
          </Tabs.Root>
        )}

        <Dialog.Footer>
          <Dialog.Close asChild>
            <Button
              data-cta='admin-document-content-modal-close-button'
              variant='secondary'
            >
              <Trans>Close</Trans>
            </Button>
          </Dialog.Close>
        </Dialog.Footer>
      </Dialog.Content>

      {hasPreviousContent && hasCurrentContent && (
        <DocumentDiffViewerModal
          open={isDiffOpen}
          onOpenChange={setIsDiffOpen}
          previousContent={previousContent ?? ''}
          currentContent={diffCurrentContent ?? ''}
          rawComparison={!!rawDiff}
        />
      )}
    </Dialog.Root>
  );
});
